import React, { useState, useEffect, useCallback } from 'react';
import {
    AnimatePresence,
    motion
} from 'framer-motion';
import {
    Box,
    Card,
    Typography,
    Button,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    History as HistoryIcon,
    DirectionsCar as CarIcon,
    CheckCircle as CheckIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { bookingService } from '../services/bookingService';
import { Booking } from '../types/booking';
import HandoverWindow from './HandoverWindow';
// import { useHandoverStore } from '../store/handoverStore';

const HandoverReminderOverlay: React.FC = () => {
    const { t } = useTranslation();
    const [delayedBookings, setDelayedBookings] = useState<Booking[]>([]);
    const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
    const CHECK_INTERVAL_MS = 60 * 60 * 1000;
    const LAST_CHECK_KEY = 'handover_reminder_last_check_at';

    // We use the store to keep track if a handover is in progress through this overlay
    // const { setBookingToHandover } = useHandoverStore();

    const fetchDelayedBookings = useCallback(async () => {
        try {
            const lastCheckAt = Number(localStorage.getItem(LAST_CHECK_KEY) || 0);
            const currentTimeMs = Date.now();

            if (lastCheckAt && currentTimeMs - lastCheckAt < CHECK_INTERVAL_MS) {
                return;
            }

            localStorage.setItem(LAST_CHECK_KEY, String(currentTimeMs));

            // Find bookings that are confirmed and should have started already
            const response = await bookingService.listBookings({
                status: 'confirmed',
                limit: 20
            });

            const currentDate = new Date();
            const delayed = response.bookings.filter((b: Booking) => {
                const startDate = new Date(b.startDate);
                // Consider delayed if start time passed by 30 mins
                return currentDate.getTime() - startDate.getTime() > 30 * 60 * 1000 && !dismissedIds.has(b._id);
            });

            setDelayedBookings(delayed);
        } catch (error) {
            console.error('Error fetching delayed handovers for reminder:', error);
        }
    }, [dismissedIds]);

    useEffect(() => {
        fetchDelayedBookings();
        const interval = setInterval(fetchDelayedBookings, CHECK_INTERVAL_MS); // Check every hour
        return () => clearInterval(interval);
    }, [fetchDelayedBookings]);

    const handleDismiss = (id: string) => {
        setDismissedIds(prev => new Set(prev).add(id));
        setDelayedBookings(prev => prev.filter(b => b._id !== id));
    };

    const handleConfirmHandover = (booking: Booking) => {
        setSelectedBooking(booking);
        setHandoverDialogOpen(true);
    };

    const handleHandoverSuccess = () => {
        if (selectedBooking) {
            handleDismiss(selectedBooking._id);
        }
        setHandoverDialogOpen(false);
        setSelectedBooking(null);
        fetchDelayedBookings();
    };

    if (delayedBookings.length === 0) return null;

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    zIndex: 2000,
                    maxWidth: 380,
                    width: 'calc(100% - 48px)'
                }}
            >
                {/* @ts-ignore */}
                <AnimatePresence>
                    {delayedBookings.slice(0, 3).map((booking, index) => (
                        <motion.div
                            key={booking._id}
                            initial={{ opacity: 0, y: 50, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 100 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card
                                sx={{
                                    p: 2.5,
                                    borderRadius: '20px',
                                    background: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: '0 12px 40px rgba(39, 59, 125, 0.18)',
                                    border: '1px solid rgba(39, 59, 125, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '6px',
                                        height: '100%',
                                        bgcolor: '#FF690F'
                                    }}
                                />

                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box
                                            sx={{
                                                bgcolor: 'rgba(255, 105, 15, 0.1)',
                                                p: 1,
                                                borderRadius: '12px',
                                                display: 'flex'
                                            }}
                                        >
                                            <HistoryIcon sx={{ color: '#FF690F', fontSize: 20 }} />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="700" color="#273B7D">
                                            {t('pos.handover.delayedReminder')}
                                        </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => handleDismiss(booking._id)} sx={{ color: '#9E9E9E' }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                                    {t('pos.handover.reminderQuestion', {
                                        bookingNumber: booking.bookingNumber || '',
                                        vehicle: typeof booking.vehicleId === 'object' ? `${booking.vehicleId.make} ${booking.vehicleId.model}` : 'Vehicle',
                                        customer: booking.customer.name
                                    })}
                                </Typography>

                                <Box sx={{ mb: 2.5, p: 1.5, bgcolor: '#F5F7FA', borderRadius: '12px', border: '1px dashed #D0D7DE' }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        <CarIcon sx={{ fontSize: 16, color: '#499FDD' }} />
                                        <Typography variant="caption" fontWeight="600" color="#499FDD">
                                            {typeof booking.vehicleId === 'object' ? booking.vehicleId.plateNumber : ''}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <TimeIcon sx={{ fontSize: 16, color: '#FF690F' }} />
                                        <Typography variant="caption" fontWeight="600" color="#FF690F">
                                            {t('pos.handover.scheduledFor')}: {new Date(booking.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" gap={1.5}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleDismiss(booking._id)}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: '#D0D7DE',
                                            color: '#667085',
                                            '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D0D7DE' }
                                        }}
                                    >
                                        {t('pos.handover.notYet')}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        startIcon={<CheckIcon />}
                                        onClick={() => handleConfirmHandover(booking)}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            bgcolor: '#273B7D',
                                            '&:hover': { bgcolor: '#1D2A4C' }
                                        }}
                                    >
                                        {t('pos.handover.yesStarted')}
                                    </Button>
                                </Box>
                            </Card>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </Box>

            {selectedBooking && handoverDialogOpen && (
                <HandoverWindow
                    open={handoverDialogOpen}
                    onClose={() => {
                        setHandoverDialogOpen(false);
                        setSelectedBooking(null);
                    }}
                    booking={selectedBooking}
                    onHandoverComplete={handleHandoverSuccess}
                />
            )}
        </>
    );
};

export default HandoverReminderOverlay;
